class ScrollUnveil {
    constructor(options = {}) {
      this.options = {
        threshold: options.threshold || 0.1,
        rootMargin: "0px",
        ...options,
      };
      this.observer = new IntersectionObserver(this.unveilElement.bind(this), this.options);
      this.groups = new Map(); // Store group configurations
      this.triggeredGroups = new Set(); // Track which groups have been triggered
      this.groupElements = new Map(); // Store elements by group name
    }
  
    // New method: Define a group with shared settings
    defineGroup(groupName, groupOptions = {}) {
      this.groups.set(groupName, {
        direction: groupOptions.direction || 'up',
        stagger: groupOptions.stagger || 100, // ms between each element
        duration: groupOptions.duration || 600,
        threshold: groupOptions.threshold || this.options.threshold,
        ...groupOptions
      });
    }
  
    // New method: Observe elements by group
    observeGroup(groupName, selector) {
      const groupConfig = this.groups.get(groupName);
      if (!groupConfig) {
        console.warn(`Group "${groupName}" not defined. Use defineGroup() first.`);
        return;
      }

      const elements = document.querySelectorAll(selector);
      
      // Store elements by group
      if (!this.groupElements.has(groupName)) {
        this.groupElements.set(groupName, []);
      }
      
      elements.forEach((el, index) => {
        // Apply group settings, but allow individual overrides
        if (!el.dataset.suDirection) el.dataset.suDirection = groupConfig.direction;
        if (!el.dataset.suDuration) el.dataset.suDuration = groupConfig.duration;
        if (!el.dataset.suThreshold) el.dataset.suThreshold = groupConfig.threshold;
        
        // Calculate staggered delay
        const baseDelay = parseInt(el.dataset.suDelay) || 0;
        const staggerDelay = index * groupConfig.stagger;
        el.dataset.suDelay = baseDelay + staggerDelay;
        
        // Mark as part of group
        el.dataset.suGroup = groupName;
        el.dataset.suGroupIndex = index;
        
        // Add to group elements array
        this.groupElements.get(groupName).push(el);
        
        this.observeElement(el);
      });
    }
  
    // Enhanced method: Observe with group detection
    observeElements(elements) {
      const groupedElements = new Map();
      const individualElements = [];
      
      // Separate grouped elements from individual elements
      elements.forEach((el) => {
        const groupName = el.dataset.suGroup;
        if (groupName) {
          if (!groupedElements.has(groupName)) {
            groupedElements.set(groupName, []);
          }
          groupedElements.get(groupName).push(el);
        } else {
          individualElements.push(el);
        }
      });
      
      // Handle grouped elements
      groupedElements.forEach((groupElements, groupName) => {
        // Auto-define group if it doesn't exist (for template-generated groups)
        if (!this.groups.has(groupName)) {
          this.defineGroup(groupName, {
            direction: 'up',
            stagger: 400, // Default to match template delays
            duration: 600
          });
        }
        
        // Store elements by group
        this.groupElements.set(groupName, groupElements);
        
        // Observe all elements in the group, but they'll coordinate through group logic
        groupElements.forEach((el) => {
          this.observeElement(el);
        });
      });
      
      // Handle individual elements
      individualElements.forEach((el) => {
        this.observeElement(el);
      });
    }
  
    setupElementInitialState(el) {
      const duration = parseInt(el.dataset.suDuration) || 600;
      const direction = el.dataset.suDirection || "none";

      el.style.opacity = 0;
      el.style.willChange = "opacity, transform";
      el.style.transform = this.getTransform(direction, 20);
      el.dataset.suProcessed = true;

      setTimeout(() => {
        el.style.transition = `opacity ${duration}ms ease-out, transform ${duration}ms ease-out`;
      }, 100);
    }

    observeElement(el) {
      const delay = parseInt(el.dataset.suDelay) || 0;
      const duration = parseInt(el.dataset.suDuration) || 600;
      const direction = el.dataset.suDirection || "none";
      const threshold = el.dataset.suThreshold ? parseFloat(el.dataset.suThreshold) : this.options.threshold;

      // If element doesn't have initial state set up yet, set it up
      if (!el.dataset.suProcessed) {
        this.setupElementInitialState(el);
      }

      const observer = new IntersectionObserver(this.unveilElement.bind(this), {
        ...this.options,
        threshold
      });

      requestAnimationFrame(() => {
        if (this.isElementInViewport(el)) {
          setTimeout(() => {
            el.style.opacity = 1;
            el.style.transform = "translate3d(0,0,0) scale(1)";
          }, delay > 0 ? delay : 100);
        } else {
          observer.observe(el);
        }
      });
    }
  
    unveilElement(entries, observer) {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          const el = entry.target;
          const groupName = el.dataset.suGroup;
          
          // If element is part of a group and group hasn't been triggered yet
          if (groupName && !this.triggeredGroups.has(groupName)) {
            this.triggerGroup(groupName);
            this.triggeredGroups.add(groupName);
          } 
          // If element is part of a group that's already been triggered, animate it immediately
          else if (groupName && this.triggeredGroups.has(groupName)) {
            const delay = parseInt(el.dataset.suDelay) || 0;
            setTimeout(() => {
              el.style.opacity = 1;
              el.style.transform = "translate3d(0,0,0) scale(1)";
            }, delay);
          }
          // If element is not part of a group, animate individually
          else if (!groupName) {
            const delay = parseInt(el.dataset.suDelay) || 0;
            setTimeout(() => {
              el.style.opacity = 1;
              el.style.transform = "translate3d(0,0,0) scale(1)";
            }, delay);
          }
          
          observer.unobserve(el);
        }
      });
    }

    // New method: Trigger all elements in a group
    triggerGroup(groupName) {
      const elements = this.groupElements.get(groupName);
      if (!elements) return;
      
      elements.forEach((el) => {
        const delay = parseInt(el.dataset.suDelay) || 0;
        setTimeout(() => {
          el.style.opacity = 1;
          el.style.transform = "translate3d(0,0,0) scale(1)";
        }, delay);
      });
    }
  
    getTransform(direction, distance) {
      const transforms = {
        none: "translate3d(0,0,0) scale(1)",
        up: `translate3d(0, ${distance}px, 0)`,
        down: `translate3d(0, -${distance}px, 0)`,
        left: `translate3d(${distance}px, 0, 0)`,
        right: `translate3d(-${distance}px, 0, 0)`,
        front: "scale(0.8)",
        bottom: "scale(1.2)",
      };
      return transforms[direction] || transforms.none;
    }
  
    isElementInViewport(el) {
      if (!el) return false;
      const rect = el.getBoundingClientRect();
      const viewportHeight = window.innerHeight || document.documentElement.clientHeight;
      return (rect.top < viewportHeight && rect.bottom > 0);
    }
  
    // New method: Get all groups
    getGroups() {
      return Array.from(this.groups.keys());
    }
  
    // New method: Remove a group definition
    removeGroup(groupName) {
      return this.groups.delete(groupName);
    }

    // New method: Force trigger all visible groups (emergency fallback)
    triggerVisibleGroups() {
      requestAnimationFrame(() => {
        const viewportHeight = window.innerHeight || document.documentElement.clientHeight;
        
        this.groupElements.forEach((elements, groupName) => {
          if (!this.triggeredGroups.has(groupName)) {
            // Check if any element in the group is in viewport (batched)
            const hasVisibleElement = elements.some(el => {
              if (!el) return false;
              const rect = el.getBoundingClientRect();
              return (rect.top < viewportHeight && rect.bottom > 0);
            });
            if (hasVisibleElement) {
              this.triggerGroup(groupName);
              this.triggeredGroups.add(groupName);
            }
          }
        });
      });
    }
  }
  
  export default ScrollUnveil;
